'use client';

import { useState, useTransition } from 'react';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Trans } from '@kit/ui/trans';

import { acceptTermsAction } from '~/home/(user)/settings/_lib/server/terms-actions';

interface TermsUpdateDialogProps {
  /** Whether the dialog should be shown (user has not accepted current terms) */
  open: boolean;
  /** Display name for the greeting */
  firstName?: string | null;
}

/**
 * TermsUpdateDialog
 *
 * Shown when the user's accepted terms version is behind CURRENT_TERMS_VERSION.
 * Mirrors Meteor's TermsUpdateDialog behavior: persistent modal that blocks
 * navigation until terms are accepted or the user logs out.
 */
export function TermsUpdateDialog({ open, firstName }: TermsUpdateDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const handleAccept = () => {
    setError(null);
    startTransition(async () => {
      try {
        const result = await acceptTermsAction();

        if (!result.success) {
          setError(result.error ?? 'Failed to accept terms. Please try again.');
        }
      } catch {
        setError('Failed to accept terms. Please try again.');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent
        className="flex max-h-[90vh] max-w-lg flex-col overflow-hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            <Trans
              i18nKey="terms:updateTitle"
              defaults="Terms & Conditions Update"
            />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-4">
          {firstName && (
            <p>
              <Trans
                i18nKey="terms:greeting"
                defaults="Hello {{firstName}},"
                values={{ firstName }}
              />
            </p>
          )}

          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="terms:updateInfo"
              defaults="We have updated our Terms & Conditions. Please review and accept the new terms to continue using Ballee."
            />
          </p>

          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="terms:questions"
              defaults="If you have any questions about these changes, please contact us at support@ballee.co."
            />
          </p>

          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 text-sm underline underline-offset-4"
          >
            <Trans
              i18nKey="terms:readTerms"
              defaults="Read the Terms & Conditions"
            />
          </a>

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button
            onClick={handleAccept}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            {isPending ? (
              <Trans i18nKey="terms:accepting" defaults="Accepting..." />
            ) : (
              <Trans i18nKey="terms:accept" defaults="I Accept" />
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
